import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faFileAlt,
  faShieldAlt,
  faPlane,
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { TravelCompletedScreenStyles } from './completedScreens.styles';
import {
  CompletedCard,
  CompletedCardHeader,
  CompletedCardIcon,
  CompletedCardTitle,
  CompletedFieldGrid,
  CompletedField,
  CompletedSectionTitle,
} from './CompletedV2.styles';
import {
  generalAlertError,
} from '../../common/utils/alerts';
import { sonnerSuccessAlert } from '../../common/utils/sonnerAlerts';
import { apiDownloadDocument, documentMethodManualUpload } from '../../services/axios-client/axeCommons';
import quotationsAxe from '../../services/axios-client/quotationsAxeClient';
import { getTravelCompletedData, travelCompletedDataReset } from '../../features/purchases/purchasesActions';
import PageLoading from '../../common/componentsV2/elements/PageLoading';
import ErrorComponent from '../../common/components/ErrorComponent';
import { productsTravel } from '../../common/constants';
import { ActionsWrapperResultPage } from '../../common/components/redesignComponents/Purchase/SignatureType.styles';
import pdfIMG from '../../assets/images/purchase/pdf.png';
import downloadIMG from '../../assets/redesign_icons/download.svg';
import uploadIcon from '../../assets/redesign_icons/upload.svg';
import ContractDocuments from '../../common/components/elements/ContractDocuments';
import CompletedQuoteScreenAutoAfterSale from '../../common/components/elements/CompletedQuoteScreenAutoAfterSale';
import axe from '../../services/axios-client/axiosClient';
import { downloadFile } from '../../services/axios-client/axeHelpers';

function TravelCompletedScreen() {
  const { quoteId } = useParams();
  const {
    loading, quote, error, insurance_request,
  } = useSelector((store) => store.purchases.travel_completed);
  const { document_types } = useSelector(
    (store) => store.afterSale,
  );
  const [residenceInfo, setResidenceInfo] = useState(null);
  const uploadPolizzaEmessa = useRef(null); // 13 - Polizza emessa
  const uploadQuietanzaSemestrale = useRef(null); // 32 - Quietanza Semestrale
  const uploadPrivacyRef = useRef(null); // 12 - Privacy Greenia
  const uploadPreContractRef = useRef(null); // 29 - Percontrattuale Greenia
  const uploadDichiarazioniDocRef = useRef(null); // 28 - Dichiarazioni del contraente
  const uploadQuestionarioAutoDocRef = useRef(null); // 30 - Questionario Auto

  const collectQuoteData = () => {
    getTravelCompletedData(quoteId);
  };

  const collectMunicipalities = () => {
    try {
      quotationsAxe.get('/form-data/communes').then((res) => {
        const commune = res.data.data.find((commune) => commune.cadastral_code == insurance_request.customer.residence_commune_code);
        setResidenceInfo(commune);
      });
    } catch (err) {
      // Do nothing
    }
  };

  const downloadInvoice = async (quoteId, payment_number) => {
    const res = await axe.get(`/list/invoices/${quoteId}/download/${payment_number}`, {
      responseType: 'blob',
    });
    const contentDisposition = res.headers['content-disposition'];
    const file_name = contentDisposition.split('filename="')[1].split('"')[0];
    downloadFile(res.data, file_name);
  };

  const downloadDocument = async (documentId) => {
    const response = await apiDownloadDocument(documentId);
    const alink = document.createElement('a');
    alink.target = '_blank';
    alink.href = response.data.data.contents;
    alink.download = response.data.data.filename;
    alink.click();
  };

  useEffect(() => {
    if (quote && quote.status === 7) {
      collectMunicipalities();
    }
  }, [quote]);

  const fireSignedDocUpload = (typeId) => {
    if ([13, 35].includes(typeId)) {
      uploadPolizzaEmessa.current.click();
    }
    if (typeId === 32) {
      uploadQuietanzaSemestrale.current.click();
    }
    if (typeId === 29) {
      uploadPreContractRef.current.click();
    }

    if (typeId === 12) {
      uploadPrivacyRef.current.click();
    }

    if (typeId === 28) {
      uploadDichiarazioniDocRef.current.click();
    }

    if (typeId === 30) {
      uploadQuestionarioAutoDocRef.current.click();
    }
  };

  const uploadSignedDocument = async (documentId, files) => {
    const uploadedFile = files[0];
    const reader = new FileReader();

    reader.readAsDataURL(uploadedFile);
    if (uploadedFile.type !== 'application/pdf') {
      generalAlertError('Formato di file errato. Il file deve essere in .pdf');
      return;
    }
    reader.onload = () => {
      documentMethodManualUpload(quote.id, documentId, {
        document: reader.result.replace('data:application/pdf;base64,', ''),
      }).then(() => {
        collectQuoteData();
        sonnerSuccessAlert('File caricato');
      });
    };
  };

  useEffect(() => {
    collectQuoteData();

    return () => {
      travelCompletedDataReset();
    };
  }, [quoteId]);

  if (loading && quote === null) {
    return <PageLoading />;
  }

  if (loading === false && error !== null) {
    return <ErrorComponent error={error} />;
  }
  // const totalGuarantees = quote.guarantees.reduce((sum, guarantee) => sum + guarantee.amount, 0);

  return (
    <TravelCompletedScreenStyles>
      {/* Contract Documents Section */}
      <div className="section-container">
        <ContractDocuments
          downloadDocument={downloadDocument}
          insuranceRequest={quote.insurance_request}
          quote={quote}
          collectQuoteData={collectQuoteData}
          v2
        />
      </div>

      {/* Customer & Travel Information Section */}
      <div className="section-container">
        <CompletedSectionTitle>
          <FontAwesomeIcon icon={faFileAlt} className="section-icon" />
          <h2>Informazioni Polizza</h2>
        </CompletedSectionTitle>

        <div className="cards-row">
          {/* Customer Information Card */}
          <div>
            <CompletedCard className="polizze-emessa-status-step-one">
              <CompletedCardHeader>
                <CompletedCardIcon>
                  <FontAwesomeIcon icon={faUser} />
                </CompletedCardIcon>
                <CompletedCardTitle>Anagrafica</CompletedCardTitle>
              </CompletedCardHeader>

              <CompletedFieldGrid>
                <CompletedField className="full">
                  <dt>Nome e Cognome</dt>
                  <dd>
                    {insurance_request.customer.name}
                    {' '}
                    {insurance_request.customer.surname}
                  </dd>
                </CompletedField>

                <CompletedField>
                  <dt>Email</dt>
                  <dd>{insurance_request.customer.email}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Telefono</dt>
                  <dd>{insurance_request.customer.phone}</dd>
                </CompletedField>

                <CompletedField className="full">
                  <dt>Indirizzo</dt>
                  <dd>
                    {insurance_request.customer.address}
                    ,
                    {' '}
                    {insurance_request.customer.house_number}
                    ,
                    {' '}
                    {insurance_request.customer.postal_code}
                  </dd>
                </CompletedField>

                <CompletedField>
                  <dt>Residenza</dt>
                  <dd>{residenceInfo !== null && residenceInfo.name}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Città</dt>
                  <dd>
                    {residenceInfo !== null && residenceInfo.communal_territory.name}
                  </dd>
                </CompletedField>
              </CompletedFieldGrid>
            </CompletedCard>
          </div>

          {/* Travel Product Data Card */}
          <div>
            <CompletedCard className="polizze-emessa-status-step-two">
              <CompletedCardHeader>
                <CompletedCardIcon>
                  <FontAwesomeIcon icon={faPlane} />
                </CompletedCardIcon>
                <CompletedCardTitle>Dati Prodotto Viaggi</CompletedCardTitle>
              </CompletedCardHeader>

              <CompletedFieldGrid>
                <CompletedField>
                  <dt>Data partenza</dt>
                  <dd>
                    {moment(insurance_request.policy_effective_data).format('DD-MM-YYYY')}
                  </dd>
                </CompletedField>

                <CompletedField>
                  <dt>Data Ritorno</dt>
                  <dd>
                    {moment(insurance_request.insurance_request_travel.end_date).format('DD-MM-YYYY')}
                  </dd>
                </CompletedField>

                <CompletedField>
                  <dt>Destinazione</dt>
                  <dd>
                    {insurance_request.insurance_request_travel.country.name}
                  </dd>
                </CompletedField>

                <CompletedField>
                  <dt>Numero di persone</dt>
                  <dd>
                    {1 + insurance_request.insurance_request_travel.insurance_request_travel_people.length}
                  </dd>
                </CompletedField>
              </CompletedFieldGrid>
            </CompletedCard>
          </div>
        </div>
      </div>

      {/* Policy Data Section */}
      <div className="section-container">
        <CompletedSectionTitle>
          <FontAwesomeIcon icon={faShieldAlt} className="section-icon" />
          <h2>Dettagli Polizza</h2>
        </CompletedSectionTitle>

        <div className="row">
          <div className="col-12 col-md-6 mb-3">
            <CompletedCard className="polizze-emessa-status-step-three">
              <CompletedCardHeader>
                <CompletedCardIcon>
                  <FontAwesomeIcon icon={faShieldAlt} />
                </CompletedCardIcon>
                <CompletedCardTitle>Dati Polizza</CompletedCardTitle>
              </CompletedCardHeader>

              <CompletedFieldGrid>
                <CompletedField>
                  <dt>Compagnia</dt>
                  <dd>{quote.company.name}</dd>
                </CompletedField>

                <CompletedField className="full">
                  <dt>Prodotto</dt>
                  <dd>
                    {productsTravel[quote.quotation_data_travel_europeassistance.product_code]}
                  </dd>
                </CompletedField>

                <CompletedField>
                  <dt>Stato</dt>
                  <dd>{quote.status === 7 && 'Polizza Emmesa'}</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Polizza Pagata</dt>
                  <dd>SI</dd>
                </CompletedField>

                <CompletedField>
                  <dt>Provvigioni</dt>
                  <dd>
                    {quote.commision.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    {' '}
                    &euro;
                  </dd>
                </CompletedField>

                <CompletedField>
                  <dt>Brokeraggio</dt>
                  <dd>
                    {quote.brokerage_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    {' '}
                    &euro;
                  </dd>
                </CompletedField>
                <CompletedField>
                  <dt>Totale</dt>
                  <dd>
                    {(Number(quote.amount)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    {' '}
                    &euro;
                  </dd>
                </CompletedField>
              </CompletedFieldGrid>
            </CompletedCard>
          </div>

          {quote.guarantees && quote.guarantees.length !== 0 && (
          <div className="col-12 col-md-6 mb-3">
            <CompletedCard className="polizze-emessa-status-step-three">
              <CompletedCardHeader>
                <CompletedCardIcon>
                  <FontAwesomeIcon icon={faShieldAlt} />
                </CompletedCardIcon>
                <CompletedCardTitle>Dati Garanzie</CompletedCardTitle>
              </CompletedCardHeader>

              <CompletedFieldGrid>
                {quote.guarantees.map((guarantee, index) => (
                  <CompletedField key={index}>
                    <dt>{guarantee.guarantees_description}</dt>
                    <dd>
                      {guarantee.amount}
                      {' '}
                      &euro;
                    </dd>
                  </CompletedField>
                ))}
              </CompletedFieldGrid>
            </CompletedCard>
          </div>
          )}
        </div>
      </div>

      {/* Documents Section */}
      <div className="section-container">
        <CompletedCard className="polizze-emessa-status-step-four">
          <CompletedCardHeader>
            <CompletedCardIcon>
              <FontAwesomeIcon icon={faFileAlt} />
            </CompletedCardIcon>
            <CompletedCardTitle>Documenti</CompletedCardTitle>
          </CompletedCardHeader>
          <ol className="list-group list-group-numbered">
            {insurance_request.documents.filter(
              (dr) => dr.document_type_id !== 34 && dr.document_type_id !== 35 && dr.document_type_id !== 36 && dr.document_type_id !== 37 && dr.document_type_id !== 38 && dr.document_type_id !== 39 && dr.document_type_id !== 40 && dr.document_type_id !== 41,
            )
              .map((document, index) => (
                <li className="list-group-item d-flex justify-content-between align-items-center" key={index}>
                  <div className="ms-2 me-auto">
                    <div className="fw-bold mb-2">{document.document_type.name}</div>
                    <div className="d-flex align-items-center justify-content-between">
                      <img src={pdfIMG} className="img-fluid" width="25" alt="PDF" />
                      <div className="mx-2">
                        <h5 className="h6 mb-0">{document.original_filename}</h5>
                      </div>
                      <div className="d-flex align-items-center justify-content-center">
                        <button
                          className="doc-btn border-0 bg-transparent"
                          onClick={() => downloadDocument(document.id)}
                        >
                          <img src={downloadIMG} width="25" alt="" />
                        </button>
                      </div>
                      {(document.signable_document === 1 && (document.is_signed_valid === 0 || (document.is_signed_valid === null && document.signed_document_filename === null))) && (
                        <ActionsWrapperResultPage>
                          {' '}
                          {([13, 35].includes(document.document_type.id)) && (
                            <div className="action-button" onClick={() => fireSignedDocUpload(document.document_type_id)}>
                              <img src={uploadIcon} alt="" width={25} />
                              <input ref={uploadPolizzaEmessa} type="file" name="" id="" className="d-none" onChange={(e) => uploadSignedDocument(document.id, e.target.files)} />

                            </div>
                          )}
                          {document.document_type.id === 32 && (
                            <div className="action-button" onClick={() => fireSignedDocUpload(document.document_type_id)}>
                              <img src={uploadIcon} alt="" width={25} />
                              <input ref={uploadQuietanzaSemestrale} type="file" name="" id="" className="d-none" onChange={(e) => uploadSignedDocument(document.id, e.target.files)} />

                            </div>
                          )}
                          {document.document_type.id === 29
                            && (
                              <div className="action-button" onClick={document.signed_document_filename === null ? () => fireSignedDocUpload(document.document_type.id) : () => { }}>
                                <img src={uploadIcon} alt="Carica" width={25} />
                                <input ref={uploadPreContractRef} type="file" name="" id="" className="d-none" onChange={(e) => uploadSignedDocument(document.id, e.target.files)} />

                              </div>
                            )}
                          {document.document_type.id === 28
                            && (
                              <div className="action-button" onClick={document.signed_document_filename === null ? () => fireSignedDocUpload(document.document_type.id) : () => { }}>
                                <img src={uploadIcon} alt="Carica" width={25} />
                                <input ref={uploadDichiarazioniDocRef} type="file" name="" id="" className="d-none" onChange={(e) => uploadSignedDocument(document.id, e.target.files)} />

                              </div>
                            )}
                          {document.document_type.id === 30
                            && (
                              <div className="action-button" onClick={document.signed_document_filename === null ? () => fireSignedDocUpload(document.document_type.id) : () => { }}>
                                <img src={uploadIcon} alt="Carica" width={25} />
                                <input ref={uploadQuestionarioAutoDocRef} type="file" name="" id="" className="d-none" onChange={(e) => uploadSignedDocument(document.id, e.target.files)} />

                              </div>
                            )}
                          {document.document_type.id === 12
                            && (
                              <div className="action-button" onClick={document.signed_document_filename === null ? () => fireSignedDocUpload(document.document_type.id) : () => { }}>
                                <img src={uploadIcon} alt="Carica" width={25} />
                                <input ref={uploadPrivacyRef} type="file" name="" id="" className="d-none" onChange={(e) => uploadSignedDocument(document.id, e.target.files)} />

                              </div>
                            )}
                        </ActionsWrapperResultPage>
                      )}
                    </div>
                    {(document.signable_document === 1 && document.is_signed_valid === 0 && document.signed_document_filename === null && document.motivation !== null) && (
                      <p className="text-danger mt-2">
                        {document.motivation}
                      </p>
                    )}
                  </div>
                  <div>
                    {document.signable_document === 1 ? (
                      document.is_signed_valid === null && document.signed_document_filename !== null ? (
                        <span className="badge bg-warning rounded-pill position-absolute top-25 end-0 me-2">In Verifica</span>
                      ) : document.is_signed_valid === 0 ? (
                        <span className="badge bg-danger rounded-pill position-absolute top-25 end-0 me-2">Firma Non Valida</span>
                      ) : document.is_signed_valid === null && document.signed_document_filename === null ? (
                        <span className="badge bg-warning rounded-pill position-absolute top-25 end-0 me-2">Da Firmare</span>
                      ) : document.is_signed_valid === 1 && document.signed_document_filename !== null ? (
                        <span className="badge bg-primary rounded-pill position-absolute top-25 end-0 me-2">Firmato</span>
                      ) : null
                    ) : (
                      <span className="badge bg-info rounded-pill position-absolute top-25 end-0 me-2">Non Richiede Firma</span>
                    )}
                    {' '}

                  </div>
                </li>
              ))}
            <li className="document-item">
              <div className="document-content flex-column gap-0">
                <div className="document-name">
                  Fattura
                </div>
                <div className="document-actions flex-row">
                  <div className="file-info">
                    <img
                      src={pdfIMG}
                      className="img-fluid"
                      width="25"
                      alt="PDF"
                    />
                    <span className="file-name">Scarica Fattura</span>
                  </div>
                  <button
                    className="doc-btn border-0 bg-transparent"
                    onClick={() => downloadInvoice(quote.id, 1)}
                  >
                    <img src={downloadIMG} width="25" alt="Scarica" />
                  </button>
                </div>
              </div>
              <div className="document-status">
                <span className="status-badge status-info">
                  Non Richiede Firma
                </span>
              </div>
            </li>
          </ol>
        </CompletedCard>
      </div>

      {/* After Sale Section */}
      <CompletedQuoteScreenAutoAfterSale quote={quote} document_types={document_types} v2 />

    </TravelCompletedScreenStyles>
  );
}

export default TravelCompletedScreen;
